import { tabOf } from './chatGroups';
import type { Chat } from '@/data/types';

/**
 * One customer, one row (Gary, 19 Oct): a customer with a WhatsApp chat and an email chat shows once in the list,
 * and the chat screen switches between the two with tabs. The conversations stay separate underneath (sending,
 * Leo, alerts); only the screen pairs them.
 *
 * Paired only on the same lead or customer record, never on an address alone, so two people who share an email
 * (a couple) are never shown as one customer. Per channel the current chat is the open one with the latest activity.
 */
export interface ChannelPair { whatsapp: Chat; email: Chat }

function newer(a: Chat, b: Chat): Chat {
  if (!!a.archived !== !!b.archived) return a.archived ? b : a;
  return (b.lastActivityAt ?? '') > (a.lastActivityAt ?? '') ? b : a;
}

/** Each paired chat's id → its pair. Chats with only one channel are not in the map. */
export function channelPairs(chats: Chat[]): Map<string, ChannelPair> {
  // Group by lead and customer together: a WhatsApp chat on the customer and an email chat on their lead join up
  // when either chat carries both (union of the two keys).
  const parent = new Map<string, string>();
  const find = (k: string): string => {
    let r = k;
    while (parent.get(r) !== r) r = parent.get(r)!;
    parent.set(k, r);
    return r;
  };
  const join = (a: string, b: string) => { const ra = find(a); const rb = find(b); if (ra !== rb) parent.set(ra, rb); };
  const keysOf = (c: Chat) => [c.leadId ? `lead:${c.leadId}` : null, c.customerId ? `cust:${c.customerId}` : null].filter((k): k is string => !!k);
  for (const c of chats) {
    const keys = keysOf(c);
    for (const k of keys) if (!parent.has(k)) parent.set(k, k);
    if (keys.length === 2) join(keys[0]!, keys[1]!);
  }

  const groups = new Map<string, { whatsapp?: Chat; email?: Chat }>();
  for (const c of chats) {
    // Facebook and Instagram chats stand on their own (Gary, 3 Nov 2026): the tabs pair WhatsApp and email only.
    if (c.dm) continue;
    const keys = keysOf(c);
    if (!keys.length) continue;
    const root = find(keys[0]!);
    const g = groups.get(root) ?? {};
    const channel = c.email ? 'email' : 'whatsapp';
    g[channel] = g[channel] ? newer(g[channel]!, c) : c;
    groups.set(root, g);
  }

  const pairs = new Map<string, ChannelPair>();
  for (const g of groups.values()) {
    if (!g.whatsapp || !g.email) continue;
    const pair = { whatsapp: g.whatsapp, email: g.email };
    pairs.set(g.whatsapp.id, pair);
    pairs.set(g.email.id, pair);
  }
  return pairs;
}

/** The other chat in a pair. */
export function otherChannel(pair: ChannelPair, chatId: string): Chat {
  return pair.whatsapp.id === chatId ? pair.email : pair.whatsapp;
}

/** Which chat stands for the customer in the list: one that owes a reply first, so an owed WhatsApp chat is never
 * hidden behind a quiet email one; otherwise the latest activity (people talking, not automatic messages). */
function standsFor(a: Chat, b: Chat, now: number): Chat {
  // Owing means the Reply tab's rule, a Leo hand-over included (review, 19 Oct: a hand-over had no timer and was missed).
  const owesA = tabOf(a, now) === 'reply';
  const owesB = tabOf(b, now) === 'reply';
  if (owesA !== owesB) return owesA ? a : b;
  return newer(a, b);
}

/**
 * The list with each pair shown once: the chat that owes a reply, else the one with the latest activity, stands
 * for the customer, carrying both chats' unread messages and a mark that the other channel exists. Counts stay
 * per conversation (the caller counts before collapsing).
 */
export function onePerCustomer(list: Chat[], pairs: Map<string, ChannelPair>, now = Date.now()): Chat[] {
  const out: Chat[] = [];
  for (const c of list) {
    const pair = pairs.get(c.id);
    if (!pair) { out.push(c); continue; }
    const other = otherChannel(pair, c.id);
    const lead = standsFor(c, other, now);
    if (lead.id !== c.id && list.some((x) => x.id === other.id)) continue; // the other one stands for them
    // A closed or archived partner adds nothing to read (Gary, 19 Oct: his row showed 3 from a closed email chat).
    const otherOpen = !other.archived && !other.closed;
    out.push({
      ...c,
      unread: c.unread + (otherOpen ? other.unread : 0),
      markedUnread: c.markedUnread || (otherOpen && other.markedUnread),
      alsoOn: c.email ? 'whatsapp' : 'email', pairedId: other.id,
    });
  }
  return out;
}

/**
 * The chats owing a reply, one per customer: the Reply tab's number, the bottom Chats badge and the browser tab title
 * all use this, so they can never disagree (Gary, 19 Oct).
 */
/**
 * Customer chats with something unread, one per customer (Gary, 20 Oct: the counts should be unread chats, so the
 * bottom Chats button, the tabs, the browser title and each row's number all tell the same story). A chat marked
 * unread counts too.
 */
export function unreadCount(open: Chat[], now: number): number {
  return onePerCustomer(open.filter((c) => !c.archived), channelPairs(open), now).filter((c) => c.unread > 0 || !!c.markedUnread).length;
}

export function replyCount(open: Chat[], now: number): number {
  const pairs = channelPairs(open);
  return onePerCustomer(open.filter((c) => !c.archived), pairs, now).filter((c) => tabOf(c, now) === 'reply').length;
}
